import React, { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  LeftSidebarView, ViewBody,
} from 'components/Sidebar'
import withLeftSidebar, { LeftSidebarProps } from 'components/Sidebar/withLeftSidebar'
import KanBoIcon from 'components/KanBoIcon'
import InfiniteScroll from 'components/InfiniteScroll'
import { useDispatch, useSelector } from 'react-redux'
import createSortedBlockersSelector from 'selectors/createSortedBlockersSelector'
import AddGlobalBlocker from './AddGlobalBlocker'
import AddOnDemandBlocker from './AddOnDemandBlocker'
import fetchBlockers from 'store/sagas/fetchBlockers'
import requestRemoveBlocker from 'store/sagas/requestRemoveBlocker'
import blockerType from 'const/blockerType'
import {
  FormGroup,
  ControlLabel,
} from 'react-bootstrap'
import Button from 'components/Button'
import EditBlocker from './EditBlocker'
import styled from 'styled-components'
import BlockerUseCount from 'components/BlockerUseCount'
import CardBlockers from 'components/CardBlockers'
import BlockerInUseRemoveView from 'components/BlockerInUseRemoveView'
import sweetalert from 'sweetalert2'
import currentUserRolesSelector from 'selectors/currentUserRoles'
import BlockerListItem from 'components/BlockerListItem'

const AddButtonContainer = styled(ViewBody)`
  margin-bottom: 10px;
`

const BLOCKER_TYPES = [
  {
    id: blockerType.GLOBAL,
    formattedMessageId: 'BLOCKERS_GLOBAL',
  },
  {
    id: blockerType.ON_DEMAND,
    formattedMessageId: 'BLOCKERS_ON_DEMAND',
  },
]

const Blockers: React.FC<LeftSidebarProps> = ({
  leftSidebar
}) => {
  const dispatch = useDispatch()

  const intl = useIntl()

  const currentUserRoles = useSelector<any, any>(currentUserRolesSelector)

  const areBlockersLoading = useSelector<any, boolean>(state => state.kanbo.blockers.isLoading)
  const areBlockersLoaded = useSelector<any, boolean>(state => state.kanbo.blockers.isLoaded)
  const blockers = useSelector(useMemo(createSortedBlockersSelector, []))

  const blockersByType = useMemo(() => {
    const blockersByType = {}

    BLOCKER_TYPES.forEach(blockerType => blockersByType[blockerType.id] = [])

    blockers.forEach(blocker => {
      if (BLOCKER_TYPES.find(BLOCKER_TYPE => BLOCKER_TYPE.id === blocker.type)) {
        blockersByType[blocker.type].push(blocker)
      }
    })

    return blockersByType
    }, [blockers]
  )

  const loadBlockers = () => {
    dispatch(fetchBlockers())
  }

  return (
    <LeftSidebarView
      title={<FormattedMessage id='BLOCKERS' />}
    >
      <InfiniteScroll
        isLoading={areBlockersLoading}
        isLoaded={areBlockersLoaded}
        onLoadMore={loadBlockers}
      >
        {BLOCKER_TYPES.map(BLOCKER_TYPE =>
          <FormGroup
            key={BLOCKER_TYPE.id}
          >
            <ViewBody>
              <ControlLabel>
                <FormattedMessage id={BLOCKER_TYPE.formattedMessageId} />
              </ControlLabel>
            </ViewBody>
            {(() => {
              if (!currentUserRoles['board-manage']) return null

              switch (BLOCKER_TYPE.id) {
                case blockerType.GLOBAL:
                  return (
                    <AddButtonContainer>
                      <Button
                        icon={<KanBoIcon name='kanbo-16-plus' />}
                        onClick={() => leftSidebar.push(AddGlobalBlocker)}
                      >
                        <FormattedMessage id='ADD' />
                      </Button>
                    </AddButtonContainer>
                  )
                case blockerType.ON_DEMAND:
                  return (
                    <AddButtonContainer>
                      <Button
                        icon={<KanBoIcon name='kanbo-16-plus' />}
                        onClick={() => leftSidebar.push(AddOnDemandBlocker)}
                      >
                        <FormattedMessage id='ADD' />
                      </Button>
                    </AddButtonContainer>
                  )
              }
            })()}
            <ul className='list-group'>
              {blockersByType[BLOCKER_TYPE.id].map(blocker =>
                <BlockerListItem
                  key={blocker.id}
                  name={blocker.name}
                  description={blocker.description}
                  subtitle={
                    !blocker.isBusy ? (
                      <BlockerUseCount blockerId={blocker.id}>
                        {({ count }) =>
                          count === undefined
                            ? <KanBoIcon spin name='kanbo-16-loading' />
                            : (count && count > 0)
                              ? (
                                <Button
                                  bsSize='xsmall'
                                  onClick={(event) => {
                                    event.stopPropagation()
                                    event.preventDefault()
  
                                    leftSidebar.push(() =>
                                      <LeftSidebarView
                                        title={blocker.name}
                                      >
                                        <CardBlockers
                                          blockerId={blocker.id}
                                        />
                                      </LeftSidebarView>
                                    )
                                }}>
                                  <FormattedMessage id='BLOCKERS_USE_COUNT' values={{ count }} />
                                </Button>
                              )
                              : <FormattedMessage id='BLOCKERS_USE_COUNT' values={{ count }} />
                        }
                      </BlockerUseCount>
                    ) : undefined
                  }
                  menu={(currentUserRoles['board-manage'] && !blocker.isBusy) ? [
                    {
                      icon: <KanBoIcon fixedWidth name='kanbo-16-remove' />,
                      title: <FormattedMessage id='REMOVE' />,
                      onClick: async () => {
                        try {
                          await sweetalert({
                            text: intl.formatMessage({
                              id: 'BLOCKERS_REMOVE_QUESTION'
                            }, {
                              name: blocker.name
                            }),
                            confirmButtonText: intl.formatMessage({ id: 'REMOVE' }),
                            cancelButtonText: intl.formatMessage({ id: 'CANCEL' }),
                            showCancelButton: true,
                            type: 'warning',
                            confirmButtonClass: 'btn btn-warning'
                          })
                        } catch {
                          return
                        }

                        try {
                          await dispatch(requestRemoveBlocker({
                            id: blocker.id,
                          }))
                        } catch (error) {
                          if (error.code === 540) { // Blockers on cards
                            leftSidebar.push(() =>
                              <BlockerInUseRemoveView
                                id={blocker.id}
                                name={blocker.name}
                                onRemove={() => {
                                  dispatch(requestRemoveBlocker({
                                    id: blocker.id,
                                    forceRemoval: true,
                                  }))
                                }}
                              />
                            )
                          } else {
                            throw error
                          }
                        }
                      }
                    }
                  ] : undefined}
                  onClick={
                    currentUserRoles['board-manage']
                      ? () => leftSidebar.push(() => <EditBlocker blockerId={blocker.id} />)
                      : undefined
                  }
                />
              )}
            </ul>
          </FormGroup>
        )}
      </InfiniteScroll>
    </LeftSidebarView>
  )
}

export default withLeftSidebar(Blockers)
